<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Button, Icon, Text } from '@Shelly/shelly-design-system';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { buildVerivoxRedirectUrl } from '../../api/verivoxRedirect';
	import {
		ENERGY_PRICE_CTA_TEXT_COLOR,
		energyPriceColorSchemes,
		type ColorSchemeKey,
	} from '../../utils/colorSchemes';
	import { transformOfferToTariff } from '../../utils/tariffTransformers';
	import {
		dismissVerivoxSurface,
		type VerivoxDismissSurface,
	} from '../../utils/verivoxDismissals';
	import { openExternalLink } from 'src/helpers/utilities';
	import type { EnergyTariff } from 'src/types/EnergyTariff';
	import EnergyTariffCard from '../../components/card/EnergyTariffCard.svelte';
	import VerivoxTariffsModal from '../../modals/tariffs/VerivoxTariffsModal.svelte';
	import VerivoxAttribution from '../../shared/VerivoxAttribution.svelte';
	import VerivoxLogoPlate from '../../shared/VerivoxLogoPlate.svelte';
	import type { VerivoxBannerLayout } from '../verivox-banner-layout';

	/**
	 * Shared Verivox offers promo chrome.
	 * Prefer VerivoxOffersBannerWidget / VerivoxOffersBannerInline at call sites.
	 * (Offers are not shown in the Notifications dropdown.)
	 */
	export let layout: Extract<VerivoxBannerLayout, 'widget' | 'inline'> = 'widget';
	export let dismissable = false;
	export let dismissSurface: VerivoxDismissSurface = 'energyDashboard';
	export let onDismiss: (() => void) | undefined;

	$: isInline = layout === 'inline';
	$: flush = true;

	let energyPriceScheme: ColorSchemeKey = 'scheme1';
	let isHidden = false;
	let isLoading = true;
	let showAllModal = false;
	let bestTariffs: EnergyTariff[] = [];
	let totalResults = 0;

	$: location = energyPriceApi.getCachedLocation();
	$: postalCode = location?.postalCode || '';
	$: locationId = location?.locationId || '';
	$: totalUsage = energyPriceApi.getCachedConsumption() ?? 2500;

	onMount(() => {
		energyPriceScheme = energyPriceColorSchemes.getCurrentScheme();
		void loadOffers();
	});

	const loadOffers = async () => {
		if (!postalCode || !locationId) {
			isLoading = false;
			bestTariffs = [];
			return;
		}

		isLoading = true;
		try {
			const res = await energyPriceApi.fetchTariffs(postalCode, locationId);
			if (res?.isok && res?.data) {
				totalResults = parseInt(res.data.totalResults || '0', 10);
				const rawOffers = res.data.offers || [];
				bestTariffs = rawOffers
					.map((offer: unknown) =>
						transformOfferToTariff(offer, {
							isSponsored: false,
							preferResponsiveMsp: true,
						}),
					)
					.filter((tariff: EnergyTariff) => !tariff.isSponsored)
					.slice(0, 3);
			} else {
				bestTariffs = [];
				totalResults = 0;
			}
		} catch (error) {
			console.error('Failed to load Verivox offers for banner:', error);
			bestTariffs = [];
			totalResults = 0;
		} finally {
			isLoading = false;
		}
	};

	const handleDismiss = async () => {
		isHidden = true;
		await dismissVerivoxSurface(dismissSurface, 'offers');
		onDismiss?.();
	};

	const handleSeeAll = () => {
		showAllModal = true;
	};

	const handleExploreMore = () => {
		if (postalCode && locationId) {
			openExternalLink(
				buildVerivoxRedirectUrl(postalCode, locationId, totalUsage, '5762', '2'),
			);
		} else {
			openExternalLink('https://www.verivox.de/?tunnel-partner5762');
		}
	};
</script>

{#if !isHidden && !isLoading && bestTariffs.length > 0}
	<div
		class="verivox-offers-outer"
		class:verivox-offers-outer--flush={flush}
		class:verivox-offers-outer--inline={isInline}
	>
		<div
			class="verivox-offers"
			class:verivox-offers--inline={isInline}
			style="background: {energyPriceColorSchemes.schemes[
				energyPriceScheme
			]}; --verivox-cta-text: {ENERGY_PRICE_CTA_TEXT_COLOR}"
		>
			<span class="verivox-offers__circle verivox-offers__circle--tr-lg" aria-hidden="true"
			></span>
			<span class="verivox-offers__circle verivox-offers__circle--tr-sm" aria-hidden="true"
			></span>

			{#if !isInline}
				<div class="verivox-offers__logo">
					<VerivoxLogoPlate compact />
				</div>
			{/if}

			{#if dismissable}
				<button
					type="button"
					class="verivox-offers__dismiss reset-button-styles-base"
					aria-label={$format('button_dismiss', { default: 'Dismiss' })}
					on:click={handleDismiss}
				>
					<Icon icon="fal fa-times" size="s" />
				</button>
			{/if}

			<div class="verivox-offers__body">
				{#if isInline}
					<div class="verivox-offers__logo-inline">
						<VerivoxLogoPlate compact />
					</div>
				{/if}

				<div class="verivox-offers__heading">
					<Icon
						icon="fas fa-bolt"
						size="s"
						class="verivox-offers__icon"
					/>
					<div>
						<Text
							styleType={isInline ? 'body-extra-small' : 'body-small'}
							class="verivox-offers__title text-bold"
						>
							{$format('energy_price_setup_prompt_title', {
								default: 'Günstige Stromtarife entdecken',
								locale: 'de',
							})}
						</Text>
						{#if totalResults > 0}
							<Text styleType="body-extra-small" class="verivox-offers__subtitle">
								{$format('energy_price_banner_subtitle', {
									default: '{count} Tarife verfügbar',
									values: { count: String(totalResults) },
									locale: 'de',
								})}
							</Text>
						{/if}
					</div>
				</div>

				{#if !isInline}
					<div class="verivox-offers__grid">
						{#each bestTariffs as tariff (tariff.id)}
							<EnergyTariffCard
								{tariff}
								variant="light"
								layout="condensed"
								currency="€"
								{postalCode}
								{locationId}
								{totalUsage}
								subPartnerId="5762"
								campaignId="2"
								backgroundGradient={energyPriceColorSchemes.schemes[energyPriceScheme]}
							/>
						{/each}
					</div>
				{/if}

				<div class="verivox-offers__footer">
					<div class="verivox-offers__actions">
						<Button
							class="verivox-offers__cta"
							size={isInline ? 's' : 'm'}
							variant="secondary"
							on:click={handleSeeAll}
						>
							{$format('button_show_offers', {
								default: 'Alle anzeigen',
								locale: 'de',
							})}
						</Button>
						{#if dismissable && !isInline}
							<Button
								class="verivox-offers__cancel"
								size="m"
								variant="secondary"
								on:click={handleDismiss}
							>
								{$format('button_cancel', {
									default: 'Abbrechen',
									locale: 'de',
								})}
							</Button>
						{/if}
					</div>
					{#if !isInline}
						<VerivoxAttribution
							on:click={(event) => {
								event.detail.preventDefault = true;
								handleExploreMore();
							}}
						/>
					{/if}
				</div>
			</div>
		</div>
	</div>
{/if}

{#if showAllModal}
	<VerivoxTariffsModal
		bind:showModal={showAllModal}
		{postalCode}
		{locationId}
		{totalUsage}
	/>
{/if}

<style>
	.verivox-offers-outer {
		padding: var(--spacing-s, 8px);
	}

	.verivox-offers-outer--flush {
		padding: 0;
	}

	.verivox-offers {
		position: relative;
		overflow: hidden;
		border-radius: 12px;
		isolation: isolate;
		min-height: 0;
		height: 100%;
		max-height: 180px;
	}

	.verivox-offers__circle {
		position: absolute;
		border-radius: 50%;
		pointer-events: none;
		z-index: 0;
	}

	.verivox-offers__circle--tr-lg {
		top: -48px;
		right: -36px;
		width: 110px;
		height: 110px;
		background: rgba(255, 196, 128, 0.4);
	}

	.verivox-offers__circle--tr-sm {
		top: -18px;
		right: 18px;
		width: 64px;
		height: 64px;
		background: rgba(255, 220, 170, 0.28);
	}

	.verivox-offers__logo {
		position: absolute;
		top: 10px;
		left: 10px;
		z-index: 2;
	}

	.verivox-offers__dismiss {
		position: absolute;
		top: 8px;
		right: 8px;
		z-index: 3;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		border-radius: 6px;
		color: #ffffff;
		cursor: pointer;
		opacity: 0.9;
	}

	.verivox-offers__dismiss:hover {
		opacity: 1;
		background-color: rgba(255, 255, 255, 0.12);
	}

	.verivox-offers__body {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 48px 14px 12px;
	}

	.verivox-offers__heading {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		padding-right: 28px;
	}

	.verivox-offers__grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 8px;
	}

	.verivox-offers__footer {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 12px;
	}

	.verivox-offers__actions {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	:global(.verivox-offers__icon) {
		color: #ffffff !important;
		flex-shrink: 0;
		margin-top: 1px;
	}

	:global(.verivox-offers__title) {
		color: #ffffff !important;
		line-height: 1.35;
	}

	:global(.verivox-offers__subtitle) {
		color: rgba(255, 255, 255, 0.85) !important;
		margin-top: 2px;
	}

	:global(.verivox-offers__cta) {
		background: rgba(255, 255, 255, 0.95) !important;
		color: var(--verivox-cta-text, #e64600) !important;
		border: none !important;
		border-radius: 8px !important;
		font-weight: 600 !important;
	}

	:global(.verivox-offers__cancel) {
		background: transparent !important;
		color: #ffffff !important;
		border: 1px solid rgba(255, 255, 255, 0.55) !important;
		border-radius: 8px !important;
		font-weight: 600 !important;
		box-shadow: none !important;
	}

	.verivox-offers__footer :global(.attribution-footer) {
		margin-top: 0;
	}

	.verivox-offers--inline {
		border-radius: 10px;
		max-height: 120px;
	}

	.verivox-offers--inline .verivox-offers__circle--tr-lg {
		width: 80px;
		height: 80px;
		top: -40px;
		right: -32px;
	}

	.verivox-offers--inline .verivox-offers__circle--tr-sm {
		width: 48px;
		height: 48px;
		top: -14px;
		right: -16px;
	}

	.verivox-offers__logo-inline {
		flex-shrink: 0;
		padding-right: 28px;
	}

	.verivox-offers__logo-inline :global(.verivox-logo-plate--compact) {
		padding: 4px 8px;
		border-radius: 7px;
	}

	.verivox-offers__logo-inline :global(.verivox-logo-plate--compact .verivox-logo-plate__img) {
		height: 16px;
		max-width: 80px;
	}

	.verivox-offers--inline .verivox-offers__dismiss {
		top: 6px;
		right: 6px;
		width: 22px;
		height: 22px;
	}

	.verivox-offers--inline .verivox-offers__body {
		padding: 8px 10px 10px;
		gap: 6px;
		justify-content: space-between;
	}

	.verivox-offers--inline .verivox-offers__heading {
		align-items: center;
		padding-right: 4px;
	}

	.verivox-offers--inline :global(.verivox-offers__cta) {
		min-height: 28px !important;
		padding: 4px 12px !important;
		font-size: 12px !important;
		border-radius: 7px !important;
	}

	@media (max-width: 767px) {
		.verivox-offers__grid {
			grid-template-columns: 1fr;
		}
	}
</style>
